import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  IconButton,
  Box,
  Card,
  CardContent,
  LinearProgress,
  Chip
} from '@mui/material';
import { 
  X,
  BarChart3,
  Users,
  Target,
  TrendingUp,
  Award,
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-react';
import Button from '../common/Button';
import { formatDateForDisplay } from '../../utils/helpers';

const ScoringOverviewModal = ({
  open,
  onClose,
  scoringData,
  isLoading = false,
  title = "Scoring Overview"
}) => {
  if (!scoringData) return null;

  const { job_title, statistics, applications } = scoringData;

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-yellow-100';
    if (score >= 40) return 'bg-orange-100';
    return 'bg-red-100';
  };

  const getStatusBadge = (status) => {

  const normalized = status?.toString().toLowerCase().replace(/\s+/g, "_");

  const statusConfig = {
    submitted: { bg: '#DBEAFE', text: '#1D4ED8', label: 'Submitted' },        // blue
    under_review: { bg: '#FEF3C7', text: '#B45309', label: 'Under Review' },  // yellow
    shortlisted: { bg: '#EDE9FE', text: '#6D28D9', label: 'Shortlisted' },    // purple
    interview: { bg: '#E0E7FF', text: '#4338CA', label: 'Interview' },        // indigo
    rejected: { bg: '#FEE2E2', text: '#B91C1C', label: 'Rejected' },          // red
    hired: { bg: '#D1FAE5', text: '#065F46', label: 'Hired' },                // green
    offer_extended: { bg: '#D1FAE5', text: '#047857', label: 'Offer Extended' },
  };

  const config = statusConfig[normalized] || { bg: '#F3F4F6', text: '#374151', label: status };

  return (
    <Chip
      label={config.label}
      size="small"
      sx={{
        backgroundColor: config.bg,
        color: config.text,
        fontWeight: 500,
      }}
    />
  );
};



  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
    >
      <DialogTitle className="border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <BarChart3 className="w-6 h-6 text-blue-600" />
            <div>
              <Typography variant="h6" className="text-secondary-900">
                {title}
              </Typography>
              <Typography variant="body2" className="text-gray-600">
                {job_title}
              </Typography>
            </div>
          </div>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ color: 'gray' }}
          >
            <X />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent className="pt-6 mt-4">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-pulse text-center">
              <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <Typography variant="body2" className="text-gray-500">
                Loading scoring data...
              </Typography>
            </div>
          </div>
        ) : (
          <Grid container spacing={3}>
            {/* Statistics Overview */}
            <Grid size={{xs: 12}}>
              <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-0">
                <CardContent className="p-6">
                  <Typography variant="h6" className="font-semibold mb-4 text-gray-900">
                    Overall Statistics
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid size={{xs: 6, sm: 3}}>
                      <div className="text-center">
                        <div className="flex items-center justify-center space-x-2 mb-2">
                          <Users className="w-5 h-5 text-blue-600" />
                          <Typography variant="h4" className="font-bold text-gray-900">
                            {statistics.total_applications}
                          </Typography>
                        </div>
                        <Typography variant="body2" className="text-gray-600">
                          Total Applications
                        </Typography>
                      </div>
                    </Grid>
                    
                    <Grid size={{xs: 6, sm: 3}}>
                      <div className="text-center">
                        <div className="flex items-center justify-center space-x-2 mb-2">
                          <Award className="w-5 h-5 text-green-600" />
                          <Typography variant="h4" className={`font-bold ${getScoreColor(statistics.average_score)}`}>
                            {statistics.average_score.toFixed(1)}
                          </Typography>
                        </div>
                        <Typography variant="body2" className="text-gray-600">
                          Average Score
                        </Typography>
                      </div>
                    </Grid>
                    
                    <Grid size={{xs: 6, sm: 3}}>
                      <div className="text-center">
                        <div className="flex items-center justify-center space-x-2 mb-2">
                          <TrendingUp className="w-5 h-5 text-purple-600" />
                          <Typography variant="h4" className={`font-bold ${getScoreColor(statistics.highest_score)}`}>
                            {statistics.highest_score.toFixed(1)}
                          </Typography>
                        </div>
                        <Typography variant="body2" className="text-gray-600">
                          Highest Score
                        </Typography>
                      </div>
                    </Grid>
                    
                    <Grid size={{xs: 6, sm: 3}}>
                      <div className="text-center">
                        <div className="flex items-center justify-center space-x-2 mb-2">
                          <Target className="w-5 h-5 text-orange-600" />
                          <Typography variant="h4" className="font-bold text-gray-900">
                            {statistics.shortlisted_count}
                          </Typography>
                        </div>
                        <Typography variant="body2" className="text-gray-600">
                          Shortlisted
                        </Typography>
                      </div>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Applications List */}
            <Grid size={{xs: 12}}>
              <Typography variant="h6" className="font-semibold mb-4 text-gray-900">
                Applicant Scores
              </Typography>
              
              <div className="space-y-4">
                {applications.map((application) => (
                  <Card key={application.id} className="border border-gray-200 hover:border-blue-200 transition-colors">
                    <CardContent className="p-4">
                      <Grid container spacing={2} alignItems="center">
                        <Grid size={{xs: 12, md: 3}}>
                          <div className="flex items-center space-x-3">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold ${getScoreBgColor(application.profile_score)} ${getScoreColor(application.profile_score)}`}>
                              {application.profile_score.toFixed(0)}
                            </div>
                            <div>
                              <Typography variant="subtitle1" className="font-semibold">
                                {application.applicant_name}
                              </Typography>
                              <div className="mt-1">
                                {getStatusBadge(application.status)}
                              </div>
                            </div>
                          </div>
                        </Grid>
                        
                        <Grid size={{xs: 12, md: 6}}>
                          <div className="space-y-2">
                            {Object.entries(application.scoring_breakdown).map(([key, value]) => (
                              <div key={key} className="flex items-center justify-between">
                                <Typography variant="body2" className="text-gray-600 capitalize">
                                  {key.replace(/_/g, ' ')}:
                                </Typography>
                                <div className="flex items-center space-x-2 w-32">
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={value} 
                                    className="flex-1"
                                    sx={{
                                      height: 6,
                                      borderRadius: 3,
                                      backgroundColor: 'rgb(229, 231, 235)',
                                      '& .MuiLinearProgress-bar': {
                                        backgroundColor: value >= 70 ? '#10B981' : value >= 40 ? '#F59E0B' : '#EF4444',
                                        borderRadius: 3
                                      }
                                    }}
                                  />
                                  <Typography variant="body2" className="font-medium w-8 text-right">
                                    {value.toFixed(0)}%
                                  </Typography>
                                </div>
                              </div>
                            ))}
                          </div>
                        </Grid>
                        
                        <Grid size={{xs: 12, md: 3}}>
                          <div className="text-right space-y-2">
                            <Typography variant="body2" className="text-gray-500">
                              Applied {formatDateForDisplay(application.created_at)}
                            </Typography>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => window.open(`/candidate-profile/${application.id}`, '_blank')}
                            >
                              View Profile
                            </Button>
                          </div>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </Grid>

            {/* Score Legend */}
            <Grid size={{xs: 12}}>
              <Card variant="outlined">
                <CardContent className="p-4">
                  <Typography variant="subtitle2" className="font-semibold mb-2">
                    Scoring Legend
                  </Typography>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span>80-100%: Excellent</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                      <span>60-79%: Good</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                      <span>40-59%: Fair</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <span>0-39%: Poor</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions className="border-t p-4">
        <div className="flex space-x-3 w-full justify-between">
          <Typography variant="body2" className="text-gray-500">
            {statistics.total_applications} applicants • Updated just now
          </Typography>
          <Button
            onClick={onClose}
          >
            Close Overview
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default ScoringOverviewModal;